import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Download, Trash2, Play, Eye } from 'lucide-react';
import { useTenantContext } from '../../contexts/TenantContext';
import { apiClient, API_BASE_URL } from '../../lib/api';
import { useAuth } from '../../hooks/useApi';
import { cognitoAuth } from '../../lib/cognito';

interface Queue {
  location_id: string;
  service_id: string;
  display_name?: string;
}

interface GenerationParams {
  tenant_id: string;
  location_id: string;
  service_id: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  num_customers: number;
  no_show_rate: number;
  requeue_rate: number;
  push_back_rate: number;
  max_requeues: number;
  concurrent_capacity: number;
  avg_service_duration: number;
  jitter_percent: number;
  random_seed?: string;
  preview_only: boolean;
  ttl_hours?: number;
}

interface RunReceipt {
  test_run_id: string;
  scenario_label: string;
  tenant_id: string;
  location_id: string;
  service_id: string;
  date_range: string;
  time_window: string;
  parameters: Record<string, any>;
  results: {
    total_records: number;
    status_counts: Record<string, number>;
    preview_only: boolean;
  };
  generated_at: string;
  generated_by: string;
  preview_records?: any[];
}

export default function SampleDataGenerator() {
  const { isSuperAdmin, user } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const activeTenantId = isSuperAdmin ? selectedTenantId : user?.tenant_from_group;
  const [queues, setQueues] = useState<Queue[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState<RunReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detailedRecords, setDetailedRecords] = useState<any[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const [params, setParams] = useState<GenerationParams>({
    tenant_id: activeTenantId || '',
    location_id: '',
    service_id: '',
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    num_customers: 50,
    no_show_rate: 15,
    requeue_rate: 10,
    push_back_rate: 5,
    max_requeues: 2,
    concurrent_capacity: 1,
    avg_service_duration: 15,
    jitter_percent: 20,
    preview_only: false
  });

  useEffect(() => {
    if (activeTenantId) {
      setParams(prev => ({ ...prev, tenant_id: activeTenantId }));
      fetchQueues();
    }
  }, [activeTenantId]);

  // Update selectedQueue when params change (for initialization)
  useEffect(() => {
    if (params.location_id && params.service_id) {
      setSelectedQueue(`${params.location_id}/${params.service_id}`);
    } else {
      setSelectedQueue('');
    }
  }, [params.location_id, params.service_id]);

  const fetchQueues = async () => {
    if (!activeTenantId) {
      console.log('No activeTenantId, skipping queue fetch');
      return;
    }
    console.log('Fetching queues for tenant:', activeTenantId);
    try {
      const response = await apiClient.getQueuesSummary(activeTenantId);
      console.log('Queue response:', response);
      const activeQueues = (response.queues || []).filter((q: any) => !q.archived);
      console.log('Active queues:', activeQueues);
      setQueues(activeQueues);
    } catch (err) {
      console.error('Failed to fetch queues:', err);
    }
  };

  const generateData = async (previewOnly: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      setReceipt(null);
      
      const token = cognitoAuth.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/tests/sample-data/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...params, preview_only: previewOnly })
      });
      
      const data = await response.json();
      
      console.log('Generate response status:', response.status);
      console.log('Generate response data:', data);
      console.log('Request params:', params);
      
      if (response.ok) {
        setReceipt(data.receipt);
      } else {
        console.error('Generation failed:', data);
        setError(data.error || `Generation failed (${response.status}): ${JSON.stringify(data)}`);
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const cleanupData = async (testRunId: string) => {
    try {
      setLoading(true);
      const token = cognitoAuth.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/tests/sample-data/cleanup`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          test_run_id: testRunId,
          tenant_id: activeTenantId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setReceipt(null);
        setError(null);
      } else {
        setError(data.error || 'Cleanup failed');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const downloadReceipt = () => {
    if (!receipt) return;
    
    const blob = new Blob([JSON.stringify(receipt, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample-data-receipt-${receipt.test_run_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cleanupAllTestData = async () => {
    if (!confirm('Are you sure you want to delete ALL test data? This will remove all records generated by the Sample Data Generator across all queues.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = cognitoAuth.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/tests/sample-data/cleanup-all`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tenant_id: activeTenantId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`Successfully deleted ${data.deleted_count || 0} test records`);
        setReceipt(null);
        setDetailedRecords([]);
        setShowDetails(false);
      } else {
        setError(data.error || 'Bulk cleanup failed');
      }
    } catch (err) {
      setError('Network error occurred during bulk cleanup');
    } finally {
      setLoading(false);
    }
  };

  const downloadRecords = (format: 'csv' | 'json') => {
    if (!detailedRecords.length || !receipt) return;

    let content: string;
    let mimeType: string;
    let extension: string;

    if (format === 'csv') {
      const headers = ['Name', 'Phone', 'Status', 'Order', 'Tenant', 'Location', 'Service', 'UUID', 'PK', 'SK', 'Joined', 'Called', 'Arrived', 'Started', 'Completed', 'No Show', 'Left', 'Strikes', 'Moved From', 'Agree Version'];
      const csvRows = [headers.join(',')];
      
      console.log('Generating CSV for', detailedRecords.length, 'records');
      detailedRecords.forEach(record => {
        console.log('Processing record:', record.name, record.phone);
        const row = [
          `"${record.name || ''}"`,
          `"${record.phone || ''}"`,
          `"${record.status || ''}"`,
          record.order_number || '',
          `"${record.tenant_id || ''}"`,
          `"${record.location_id || ''}"`,
          `"${record.service_id || ''}"`,
          `"${record.uuid || ''}"`,
          `"${record.pk || ''}"`,
          `"${record.sk || ''}"`,
          record.joined_at ? new Date(record.joined_at * 1000).toISOString() : '',
          record.called_at ? new Date(record.called_at * 1000).toISOString() : '',
          record.arrived_at ? new Date(record.arrived_at * 1000).toISOString() : '',
          record.started_at ? new Date(record.started_at * 1000).toISOString() : '',
          record.completed_at ? new Date(record.completed_at * 1000).toISOString() : '',
          record.no_show_at ? new Date(record.no_show_at * 1000).toISOString() : '',
          record.left_at ? new Date(record.left_at * 1000).toISOString() : '',
          record.strike_count || 0,
          `"${record.moved_from || ''}"`,
          `"${record.agree_version || ''}"`
        ];
        csvRows.push(row.join(','));
      });
      console.log('CSV generation complete, total rows:', csvRows.length);
      
      content = csvRows.join('\n');
      mimeType = 'text/csv';
      extension = 'csv';
    } else {
      content = JSON.stringify(detailedRecords, null, 2);
      mimeType = 'application/json';
      extension = 'json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample-data-records-${receipt.test_run_id}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchDetailedRecords = async (testRunId: string) => {
    try {
      setLoadingDetails(true);
      setError(null);
      
      // Check if we have preview records in the receipt
      if (receipt?.preview_records && receipt.preview_records.length > 0) {
        setDetailedRecords(receipt.preview_records);
        setShowDetails(true);
        return;
      }
      
      // Otherwise fetch from database
      const token = cognitoAuth.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/tests/sample-data/details?test_run_id=${testRunId}&tenant_id=${activeTenantId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const records = data.records || [];
        setDetailedRecords(records);
        if (records.length === 0) {
          setError('No records found for this test run. The data may have been generated as preview-only, already cleaned up, or not yet generated.');
        } else {
          setShowDetails(true);
        }
      } else {
        setError(data.error || 'Failed to fetch detailed records');
      }
    } catch (err) {
      setError('Network error occurred while fetching details');
    } finally {
      setLoadingDetails(false);
    }
  };



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Sample Data Generator</h1>
        <div className="text-sm text-gray-500">
          Tenant: <span className="font-medium">{activeTenantId}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Generation Parameters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Queue Selection */}
            <div>
              <Label htmlFor="queue">Select Queue</Label>
              <Select
                value={selectedQueue}
                onValueChange={(value) => {
                  setSelectedQueue(value);
                  const [locationId, serviceId] = value.split('/');
                  setParams(prev => ({ 
                    ...prev, 
                    location_id: locationId,
                    service_id: serviceId
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={queues.length === 0 ? "No queues available" : "Select a queue"} />
                </SelectTrigger>
                <SelectContent>
                  {queues.map((queue) => (
                    <SelectItem key={`${queue.location_id}/${queue.service_id}`} value={`${queue.location_id}/${queue.service_id}`}>
                      {queue.location_id} / {queue.service_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-gray-500 mt-1">
                Tenant: {activeTenantId || 'None'} | Queues found: {queues.length}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={params.start_date}
                  onChange={(e) => setParams(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={params.end_date}
                  onChange={(e) => setParams(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Time Window */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={params.start_time}
                  onChange={(e) => setParams(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={params.end_time}
                  onChange={(e) => setParams(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>

            {/* Customer Count */}
            <div>
              <Label htmlFor="num_customers">Number of Customers</Label>
              <Input
                id="num_customers"
                type="number"
                min="1"
                max="2000"
                value={params.num_customers}
                onChange={(e) => setParams(prev => ({ ...prev, num_customers: parseInt(e.target.value) || 0 }))}
              />
            </div>

            {/* Behavior Rates */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="no_show_rate">No-Show Rate: {params.no_show_rate}%</Label>
                <Input
                  id="no_show_rate"
                  type="range"
                  min="0"
                  max="50"
                  value={params.no_show_rate}
                  onChange={(e) => setParams(prev => ({ ...prev, no_show_rate: parseInt(e.target.value) }))}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="requeue_rate">Requeue Rate: {params.requeue_rate}%</Label>
                <Input
                  id="requeue_rate"
                  type="range"
                  min="0"
                  max="30"
                  value={params.requeue_rate}
                  onChange={(e) => setParams(prev => ({ ...prev, requeue_rate: parseInt(e.target.value) }))}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="push_back_rate">Push Back Rate: {params.push_back_rate}%</Label>
                <Input
                  id="push_back_rate"
                  type="range"
                  min="0"
                  max="20"
                  value={params.push_back_rate}
                  onChange={(e) => setParams(prev => ({ ...prev, push_back_rate: parseInt(e.target.value) }))}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Service Configuration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="concurrent_capacity">Concurrent Capacity</Label>
                <Input
                  id="concurrent_capacity"
                  type="number"
                  min="1"
                  max="10"
                  value={params.concurrent_capacity}
                  onChange={(e) => setParams(prev => ({ ...prev, concurrent_capacity: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div>
                <Label htmlFor="avg_service_duration">Avg Service Duration (min)</Label>
                <Input
                  id="avg_service_duration"
                  type="number"
                  min="1"
                  max="120"
                  value={params.avg_service_duration}
                  onChange={(e) => setParams(prev => ({ ...prev, avg_service_duration: parseInt(e.target.value) || 15 }))}
                />
              </div>
            </div>

            {/* Advanced Options */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="jitter_percent">Service Duration Jitter: {params.jitter_percent}%</Label>
                <Input
                  id="jitter_percent"
                  type="range"
                  min="0"
                  max="50"
                  step="5"
                  value={params.jitter_percent}
                  onChange={(e) => setParams(prev => ({ ...prev, jitter_percent: parseInt(e.target.value) }))}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="random_seed">Random Seed (optional)</Label>
                <Input
                  id="random_seed"
                  placeholder="Leave empty for random"
                  value={params.random_seed || ''}
                  onChange={(e) => setParams(prev => ({ ...prev, random_seed: e.target.value || undefined }))}
                />
              </div>
              
              <div>
                <Label htmlFor="ttl_hours">Auto-cleanup after (hours)</Label>
                <Input
                  id="ttl_hours"
                  type="number"
                  min="1"
                  max="168"
                  placeholder="Optional"
                  value={params.ttl_hours || ''}
                  onChange={(e) => setParams(prev => ({ ...prev, ttl_hours: parseInt(e.target.value) || undefined }))}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => generateData(true)}
                disabled={loading || !selectedQueue}
                variant="outline"
                className="flex-1"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview
              </Button>
              <Button
                onClick={() => generateData(false)}
                disabled={loading || !selectedQueue}
                className="flex-1"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Generate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Results</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {receipt && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">Run Summary</h3>
                  <div className="text-sm space-y-1">
                    <div><strong>Test Run ID:</strong> {receipt.test_run_id}</div>
                    <div><strong>Scenario:</strong> {receipt.scenario_label}</div>
                    <div><strong>Date Range:</strong> {receipt.date_range}</div>
                    <div><strong>Time Window:</strong> {receipt.time_window}</div>
                    <div><strong>Generated:</strong> {new Date(receipt.generated_at).toLocaleString()}</div>
                    <div><strong>By:</strong> {receipt.generated_by}</div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">Results</h3>
                  <div className="text-sm space-y-1">
                    <div><strong>Total Records:</strong> {receipt.results.total_records}</div>
                    <div><strong>Preview Only:</strong> {receipt.results.preview_only ? 'Yes' : 'No'}</div>
                  </div>
                  
                  {Object.keys(receipt.results.status_counts).length > 0 && (
                    <div className="mt-3">
                      <div className="font-medium mb-1">Status Breakdown:</div>
                      {Object.entries(receipt.results.status_counts).map(([status, count]) => (
                        <div key={status} className="text-sm">
                          <strong>{status}:</strong> {count}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={downloadReceipt}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Receipt
                  </Button>
                  
                  <Button
                    onClick={() => fetchDetailedRecords(receipt.test_run_id)}
                    variant="outline"
                    size="sm"
                    disabled={loadingDetails}
                  >
                    {loadingDetails ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    View Details
                  </Button>
                  
                  {!receipt.results.preview_only && (
                    <Button
                      onClick={() => cleanupData(receipt.test_run_id)}
                      variant="destructive"
                      size="sm"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Cleanup Data
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!receipt && !error && (
              <div className="text-center text-gray-500 py-8">
                Configure parameters and click Preview or Generate to see results
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Records View */}
      {showDetails && detailedRecords.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Detailed Records ({detailedRecords.length})
              <div className="flex gap-2">
                <Button
                  onClick={() => downloadRecords('csv')}
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Button
                  onClick={() => downloadRecords('json')}
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  JSON
                </Button>
                <Button
                  onClick={() => setShowDetails(false)}
                  variant="outline"
                  size="sm"
                >
                  Hide Details
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {detailedRecords.map((record, index) => (
                  <div key={index} className="border rounded p-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><strong>Name:</strong> {record.name}</div>
                      <div><strong>Phone:</strong> {record.phone}</div>
                      <div><strong>Status:</strong> <span className={`px-2 py-1 rounded text-xs ${
                        record.status === 'completed' ? 'bg-green-100 text-green-800' :
                        record.status === 'being_serviced' ? 'bg-blue-100 text-blue-800' :
                        record.status === 'called' ? 'bg-yellow-100 text-yellow-800' :
                        record.status === 'no_show' ? 'bg-red-100 text-red-800' :
                        record.status === 'waiting' ? 'bg-gray-100 text-gray-800' :
                        record.status === 'left' ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{record.status}</span></div>
                      <div><strong>Order:</strong> #{record.order_number}</div>
                      <div><strong>Joined:</strong> {new Date(record.joined_at * 1000).toLocaleString()}</div>
                      {record.completed_at && (
                        <div><strong>Completed:</strong> {new Date(record.completed_at * 1000).toLocaleString()}</div>
                      )}
                      {record.strike_count > 0 && (
                        <div><strong>Strikes:</strong> {record.strike_count}</div>
                      )}
                      {record.moved_from && (
                        <div className="col-span-2"><strong>Moved From:</strong> {record.moved_from}</div>
                      )}
                      <div className="col-span-2">
                        <strong>Service History:</strong>
                        <div className="mt-1 space-y-1">
                          {(() => {
                            const history = [];
                            if (record.joined_at) history.push({ status: 'waiting', timestamp: record.joined_at });
                            if (record.called_at) history.push({ status: 'called', timestamp: record.called_at });
                            if (record.arrived_at || record.started_at) history.push({ status: 'being_serviced', timestamp: record.arrived_at || record.started_at });
                            if (record.completed_at) history.push({ status: 'completed', timestamp: record.completed_at });
                            if (record.no_show_at) history.push({ status: 'no_show', timestamp: record.no_show_at });
                            if (record.left_at) history.push({ status: 'left', timestamp: record.left_at });
                            
                            return history.map((step, idx) => (
                              <div key={idx} className="text-xs bg-gray-50 p-1 rounded flex justify-between">
                                <span className={`px-1 rounded ${
                                  step.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  step.status === 'being_serviced' ? 'bg-blue-100 text-blue-700' :
                                  step.status === 'called' ? 'bg-yellow-100 text-yellow-700' :
                                  step.status === 'no_show' ? 'bg-red-100 text-red-700' :
                                  step.status === 'left' ? 'bg-gray-100 text-gray-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{step.status}</span>
                                <span>{new Date(step.timestamp * 1000).toLocaleTimeString()}</span>
                              </div>
                            ));
                          })()
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Cleanup Section */}
      <Card>
        <CardHeader>
          <CardTitle>Bulk Cleanup</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Clean up ALL test data generated by the Sample Data Generator across all queues and test runs.
            </div>
            <Button
              onClick={cleanupAllTestData}
              variant="destructive"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Cleanup All Test Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Safety Notice */}
      <Alert>
        <AlertDescription>
          <strong>Safety Notice:</strong> This tool generates test data directly in the production database. 
          All generated records are tagged with <code>generated: true</code> and can be cleaned up using the test run ID. 
          Use Preview mode first to validate your configuration.
        </AlertDescription>
      </Alert>
    </div>
  );
}